import {
  getPrimaryColor,
  getPrimaryOpaqueColor,
  isMobileAtom,
  useRecoil,
} from '@orcar/common-d2c';
import styled from 'styled-components';
import {
  getInsuranceName,
  getPurchasedInsuranceDisplayType,
} from '@/utils/insurance';
import TableRowForSingleTable from './TableRowForSingleTable';

const app = import.meta.env.VITE_PACKAGE_NAME;

type SingleInsuranceTableProps = {
  isFullCoverage?: boolean;
  insurance?: string;
  companyName: string;
  coverageLimit: string;
  deductible: string;
  lossOfUseFee: string;
};

const SingleInsuranceTable = ({
  isFullCoverage,
  insurance,
  companyName,
  coverageLimit,
  deductible,
  lossOfUseFee,
}: SingleInsuranceTableProps) => {
  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const displayInsurance = getPurchasedInsuranceDisplayType(
    insurance,
    isFullCoverage,
  );
  const shouldUseSpecialContractRows = displayInsurance !== insurance;

  return (
    <Table cellSpacing='0'>
      <colgroup>
        <col width={isMobile ? '120px' : '240px'} />
        <Column isCenter width={isMobile ? '200px' : '432px'} />
      </colgroup>

      <thead>
        <tr>
          <TableHeader isMobile={isMobile}>보상 내용</TableHeader>
          <TableHeader
            isMobile={isMobile}
            fontColor={getPrimaryColor(app)}
            backgroundColor={getPrimaryOpaqueColor(app, true)}
          >
            {getInsuranceName(displayInsurance)}
          </TableHeader>
        </tr>
      </thead>

      <tbody>
        <TableRowForSingleTable
          insuranceType={displayInsurance}
          companyName={companyName}
          value={deductible}
          isFullCoverage={shouldUseSpecialContractRows}
        >
          자기부담금
        </TableRowForSingleTable>

        <TableRowForSingleTable
          insuranceType={displayInsurance}
          companyName={companyName}
          value={coverageLimit}
          isFullCoverage={shouldUseSpecialContractRows}
        >
          보상 한도
        </TableRowForSingleTable>

        <TableRowForSingleTable
          insuranceType={displayInsurance}
          companyName={companyName}
          value={lossOfUseFee}
          isFullCoverage={shouldUseSpecialContractRows}
          hasTooltip
        >
          휴차 보상료
        </TableRowForSingleTable>

        <TableRowForSingleTable
          insuranceType={displayInsurance}
          companyName={companyName}
          isFullCoverage={shouldUseSpecialContractRows}
          hasTooltip
        >
          단독 사고
        </TableRowForSingleTable>

        <TableRowForSingleTable
          insuranceType={displayInsurance}
          isFullCoverage={shouldUseSpecialContractRows}
          hasTooltip
        >
          휠/타이어
        </TableRowForSingleTable>
      </tbody>
    </Table>
  );
};

export { SingleInsuranceTable };

const Table = styled.table`
  position: relative;
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin-bottom: 12px;
  background-color: ${({ theme }) => theme.COLOR.GREY_OPAQUE};
`;

const TableHeader = styled.th<{
  selected?: boolean;
  isMobile: boolean;
  fontColor?: string;
  backgroundColor?: string;
}>`
  text-align: left;
  height: ${({ isMobile }) => (isMobile ? '40px' : '50px')};
  padding: ${({ isMobile }) => (isMobile ? '0 8px' : '0 20px')};
  border-top: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  border-bottom: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  color: ${({ fontColor, theme }) =>
    fontColor ? fontColor : theme.COLOR.GREY_01};
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor ? backgroundColor : theme.COLOR.GREY_BG};
  ${({ isMobile, theme }) => isMobile && theme.FONT.B2};
  font-weight: 600;
`;

const Column = styled.col<{
  selected?: boolean;
  isCenter?: boolean;
  isLock?: boolean;
}>`
  border-left: ${({ isCenter, theme }) =>
    isCenter && `1px solid ${theme.COLOR.GREY_05}`};
`;
